import Decimal from "decimal.js-light";
import { type BTFileSystem } from "legacyComponents/FileUploadContainer.types";
import {
    LineItemType,
    PriceTypes,
    RelatedStackItem,
} from "legacyComponents/LineItemContainer.types";

import { InvoiceFormat } from "@btnet/services-btnet/handlers/models/invoiceFormat";
import { InvoiceLineItemGroupType } from "@btnet/services-btnet/handlers/models/invoiceLineItemGroupType";
import type { InvoiceLineItemMetadata } from "@btnet/services-btnet/handlers/models/invoiceLineItemMetadata";
import { MarkupColumnTypes } from "@btnet/services-btnet/handlers/models/markupColumnTypes";

import { type TimeLocale } from "helpers/AppProvider.types";

import { type BTSelectItem } from "types/apiResponse/apiResponse";

import {
    calculateBuilderCost,
    newLineItemDefaults,
    recalculateAndSetMarkupFields,
} from "utilities/lineItem/lineItemHelper";
import { recalculateAllFields } from "utilities/lineItem/markupUtility";
import {
    add,
    divide,
    multiply,
    round,
    subtract,
    sum,
    sumListValue,
    sumNullable,
} from "utilities/math/math";
import { isNullOrUndefined } from "utilities/object/object";
import { isNullOrWhitespace } from "utilities/string/string";
import { calculateTotalIncludingTax, getTotalTaxesOnEntity } from "utilities/tax/tax.utilities";

import {
    type ILineItemsToInvoiceFormValues,
    isBillableTimeClockLine,
    type LineItemForInvoice,
} from "commonComponents/entity/invoicing/LineItemsToInvoice/LineItemsToInvoice.api.types";
import { RelatedItemType } from "commonComponents/entity/relatedItem/RelatedItem.types";
import { type TaxGroupServiceListItemExtraData } from "commonComponents/financial/TaxRateSelect/TaxRateSelect.api.types";

import { type ILineItemRelatedItem } from "entity/hawk/api/lineItem.api.types";
import { type IOwnerInvoiceFormValues } from "entity/ownerInvoice/OwnerInvoice/OwnerInvoice.api.types";
import {
    type IOwnerInvoiceLineItem,
    OwnerInvoiceLineItem,
} from "entity/ownerInvoice/OwnerInvoiceLineItemContainer/OwnerInvoiceLineItemContainer.types";
import { getStoredMaterialsThisPeriod } from "entity/ownerInvoice/OwnerInvoicePrint/OwnerInvoicePrint.utility";
import { JobTaxVersion, TaxMethod } from "entity/tax/common/tax.types";
import { NoTaxId } from "entity/tax/TaxRate/TaxRate.api.types";

/**
 * Checks if there are line items to be added to the invoice from another entity (e.g., estimate, change order, etc.)
 * @param lineItemsToInvoice The line items to invoice form values
 * @returns True if there are line items to invoice, false otherwise
 */
export function hasLineItemsToInvoice(lineItemsToInvoice?: ILineItemsToInvoiceFormValues): boolean {
    return !!lineItemsToInvoice && lineItemsToInvoice.lineItems.length > 0;
}

/**
 * Whether any line item is stacked from a timeclock shift.
 *
 * Keyed off each stack link's own `relatedLineItemType` rather than the mere presence of links
 * (ado-286675): stacking is being opened up to other entities, and everything this gates is
 * labor-cost specific, so another stackable entity's links must not light it up.
 */
export function hasTimeCardStackLinks(lineItems: IOwnerInvoiceLineItem[]): boolean {
    return lineItems.some((li) =>
        Object.values(li.relatedStackLinks ?? {}).some((stackItems) =>
            stackItems.some(
                (stackItem) => stackItem.relatedLineItemType === LineItemType.TimeCardItem,
            ),
        ),
    );
}

/**
 * Returns the job's default tax group when a no-tax invoice should "flip on" tax
 * because a taxable line is being added; otherwise undefined. Callers pre-filter
 * `candidateLines` to the lines that should count.
 */
export function getInvoiceTaxUpgradeForSelections(
    invoiceTaxGroupId: number | undefined,
    jobDefaultTaxGroupId: number | null | undefined,
    candidateLines: readonly { taxGroupId?: number | null }[],
): number | undefined {
    if (invoiceTaxGroupId !== undefined && invoiceTaxGroupId !== NoTaxId) {
        return undefined;
    }
    if (jobDefaultTaxGroupId == null || jobDefaultTaxGroupId === NoTaxId) {
        return undefined;
    }
    const hasTaxable = candidateLines.some(
        (li) => li.taxGroupId != null && li.taxGroupId !== NoTaxId,
    );
    return hasTaxable ? jobDefaultTaxGroupId : undefined;
}

interface IInvoiceForReconciliationTax {
    taxGroupId?: number;
    jobDefaultTaxGroupId?: number | null;
    taxGroups?: BTSelectItem<TaxGroupServiceListItemExtraData>[];
    taxMethod?: BTSelectItem[];
    ownerInvoiceLineItems: IOwnerInvoiceLineItem[];
}

/**
 * Reconciliation/location-state flow: upgrades the invoice tax group to the job
 * default if needed, then stamps tax onto each reconciliation line. When
 * `taxesOptOut` is true, clears incoming line tax ids and skips the rest.
 * Mutates `invoice` and matching line items in place.
 */
export function applyInvoiceTaxToReconciliationLineItems(
    invoice: IInvoiceForReconciliationTax,
    reconciliationItemIds: number[],
    taxesOptOut: boolean = false,
): void {
    const idSet = new Set(reconciliationItemIds);
    const reconciliationLines = invoice.ownerInvoiceLineItems.filter((li) => idSet.has(li.id));

    // Builder has opted out of taxes — clear any incoming tax info; no upgrade,
    // no per-line tax stamping.
    if (taxesOptOut) {
        reconciliationLines.forEach((li) => {
            li.taxGroupId = undefined;
            li.totalWithTax = li.ownerPrice;
        });
        return;
    }

    // 1) Upgrade the invoice's tax group to the job default if needed.

    const upgradedTaxGroupId = getInvoiceTaxUpgradeForSelections(
        invoice.taxGroupId,
        invoice.jobDefaultTaxGroupId,
        reconciliationLines,
    );
    if (upgradedTaxGroupId !== undefined) {
        invoice.taxGroupId = upgradedTaxGroupId;
        invoice.taxMethod?.forEach((item) => {
            item.selected = item.value === TaxMethod.Exclusive;
        });
    }

    // 2) Stamp tax onto each reconciliation line based on its taxable yes/no signal.
    const invoiceHasTax = invoice.taxGroupId !== undefined && invoice.taxGroupId !== NoTaxId;
    const taxGroup = invoiceHasTax
        ? invoice.taxGroups?.find((x) => x.value === invoice.taxGroupId)
        : undefined;
    const rate = taxGroup?.extraData?.totalEffectiveRate ?? 0;
    reconciliationLines.forEach((li) => {
        const lineIsTaxable = li.taxGroupId != null && li.taxGroupId !== NoTaxId;
        if (taxGroup && lineIsTaxable) {
            li.taxGroupId = taxGroup.value as number;
            li.totalWithTax = calculateTotalIncludingTax(li.ownerPrice, rate);
        } else {
            li.taxGroupId = NoTaxId;
            li.totalWithTax = li.ownerPrice;
        }
    });
}

export function haveLineItemsChanged(
    old: IOwnerInvoiceLineItem[],
    updated: IOwnerInvoiceLineItem[],
) {
    if (old.length !== updated.length) {
        return true;
    }
    let i = 0;
    let changes = false;
    for (const li of old) {
        const newLineItem = updated[i];
        if (
            li.id !== newLineItem.id ||
            li.ownerPrice !== newLineItem.ownerPrice ||
            li.totalWithTax !== newLineItem.totalWithTax ||
            li.taxGroupId !== newLineItem.taxGroupId ||
            li.quantity !== newLineItem.quantity ||
            li.costCodeId !== newLineItem.costCodeId ||
            li.unitCost !== newLineItem.unitCost
        ) {
            changes = true;
            break;
        }
        i++;
    }
    return changes;
}

const lineItemTypeToRelatedItemType: Partial<Record<LineItemType, RelatedItemType>> = {
    [LineItemType.Bill]: RelatedItemType.Bill,
    [LineItemType.SelectionOption]: RelatedItemType.SelectionOption,
};

/**
 * The foreign-key related-entity tag types to stamp on newly added (pre-save) invoice lines, mirroring the
 * server gate in `InvoiceManager.GetInvoiceLineItemRelatedEntitiesAsync`: flag on, standard invoices only,
 * and Selection / Estimate tags only for viewers who can see those tabs. Accounting cost details need no
 * tab permission.
 */
export function getRelatedEntityTagTypes(
    isRelatedEntityTagsFlagOn: boolean,
    invoiceFormat: InvoiceFormat | undefined,
    canViewSelections: boolean,
    canViewEstimates: boolean,
): RelatedItemType[] {
    if (
        !isRelatedEntityTagsFlagOn ||
        (invoiceFormat ?? InvoiceFormat.Invoice) !== InvoiceFormat.Invoice
    ) {
        return [];
    }

    return [
        canViewSelections && RelatedItemType.SelectionChoice,
        canViewEstimates && RelatedItemType.Estimate,
        RelatedItemType.OtherAccountingCost,
    ].filter(Boolean) as RelatedItemType[];
}

/**
 * Builds the pre-save Selection, Estimate, or Accounting Cost tag for a line so it matches what the server
 * emits after save (`LineItemsEngine.AddRelatedItemsToMarkupLineItem`). The selection choice is the line's
 * parent entity; an estimate line or accounting cost is the source line itself.
 */
function buildRelatedEntityTagFromLineItemForInvoice(
    mappedLineItem: LineItemForInvoice,
    relatedEntityTagTypes: RelatedItemType[],
): ILineItemRelatedItem | undefined {
    switch (mappedLineItem.lineItemType) {
        case LineItemType.SelectionChoice:
            // Selections Reimagined lines carry a SelectionOptionID and are tagged as a selection option instead.
            if (
                !relatedEntityTagTypes.includes(RelatedItemType.SelectionChoice) ||
                !mappedLineItem.parentEntity ||
                mappedLineItem.selectionOptionID != null
            ) {
                return undefined;
            }
            return {
                id: Number(mappedLineItem.parentEntity.id),
                type: RelatedItemType.SelectionChoice,
                title: mappedLineItem.parentEntity.title,
            };
        case LineItemType.EstimateLineItem:
            if (!relatedEntityTagTypes.includes(RelatedItemType.Estimate)) {
                return undefined;
            }
            return {
                id: mappedLineItem.lineItemId,
                type: RelatedItemType.Estimate,
                // The server falls back to the description for legacy estimate lines with no title.
                title: isNullOrWhitespace(mappedLineItem.title)
                    ? mappedLineItem.description
                    : mappedLineItem.title,
            };
        case LineItemType.OtherAccountingCost:
            if (!relatedEntityTagTypes.includes(RelatedItemType.OtherAccountingCost)) {
                return undefined;
            }
            return {
                id: mappedLineItem.lineItemId,
                type: RelatedItemType.OtherAccountingCost,
                title: mappedLineItem.title,
            };
        default:
            return undefined;
    }
}

function buildRelatedItemsFromLineItemForInvoice(
    mappedLineItem: LineItemForInvoice,
    relatedEntityTagTypes: RelatedItemType[] = [],
): ILineItemRelatedItem[] | undefined {
    const relatedEntityTag = buildRelatedEntityTagFromLineItemForInvoice(
        mappedLineItem,
        relatedEntityTagTypes,
    );
    if (relatedEntityTag) {
        return [relatedEntityTag];
    }

    const relatedItemType = lineItemTypeToRelatedItemType[mappedLineItem.lineItemType];
    if (!relatedItemType || !mappedLineItem.parentEntity) {
        return undefined;
    }

    return [
        {
            id: Number(mappedLineItem.parentEntity.id),
            type: relatedItemType,
            title: mappedLineItem.parentEntity.title,
        },
    ];
}

/**
 * Builds the pre-save time card tags for a stacked line from the `relatedStackLinks` it already
 * carries — one tag per contributing time card (the outer key), mirroring the server's emission in
 * `AddRelatedItemsToMarkupLineItem`. Time card provenance is not a single `parentEntity` like bills;
 * it lives only in these stack links, so it needs its own builder rather than the type map.
 */
function buildTimeCardRelatedItemsFromStackLinks(
    relatedStackLinks: Record<number, RelatedStackItem[]> | null | undefined,
): ILineItemRelatedItem[] {
    if (!relatedStackLinks) {
        return [];
    }

    return Object.entries(relatedStackLinks)
        .filter(([, stackItems]) => stackItems.length > 0)
        .map(([timeCardId, stackItems]) => ({
            id: Number(timeCardId),
            type: RelatedItemType.TimeCard,
            title: stackItems[0].title,
        }));
}

/**
 * Pins a single billable shift to its `$/unit` markup — the line carries one real rate, so the
 * markup is the gap between that rate and the cost rate. It is billed at
 * `unitCost + markupPerUnit` per unit.
 *
 * Uses `recalculateAllFields` rather than the deprecated `recalculateAndSetMarkupFields` the rest
 * of the cost-plus branch uses. The deprecated routine computes the client price as
 * `round(builderCost) + round(markupAmount)`, and on a fractional quantity both halves round up
 * independently: a 15 minute shift at $45.50 cost billed at $60 comes out $15.01 instead of $15.00,
 * because 11.375 → 11.38 and 3.625 → 3.63. `recalculateAllFields` multiplies the rate by the
 * quantity and rounds once, so the client is billed the rate they were quoted.
 *
 * The builder cost is not taken from the recalculation: it arrives from the handler as the
 * OT-respecting `CalculatedAmount`, which is deliberately not `unitCost * quantity` when overtime
 * multipliers apply (the handler back-solves `unitCost` from it, so recomputing drifts by up to a
 * few cents). It is kept as sent and the markup is re-derived against it, which also keeps
 * builderCost + markupAmount reconciling to the client price.
 */
function setBillableShiftMarkupFields(lineItem: OwnerInvoiceLineItem, markupPerUnit: number): void {
    lineItem.markupType = MarkupColumnTypes.UnitPrice;
    lineItem.markupPerUnit = markupPerUnit;

    const builderCostFromSource = lineItem.builderCost;
    const calculated = recalculateAllFields(lineItem);

    lineItem.ownerPrice = calculated.ownerPrice;
    lineItem.totalWithTax = calculated.totalWithTax;
    lineItem.markupPerUnit = calculated.markupPerUnit;
    lineItem.markupPercent = calculated.markupPercent;
    // margin is optional on ICalculatedFields; the UnitPrice branch always returns one.
    lineItem.margin = calculated.margin ?? 0;
    lineItem.builderCost = builderCostFromSource;
    lineItem.markupAmount = round(subtract(calculated.ownerPrice, builderCostFromSource), 2);
}

/** The totals a stack consolidates from its members. Fixed inputs — never re-derived from a stack. */
interface IConsolidatedStackTotals {
    quantity: number;
    builderCost: number;
    ownerPrice: number;
    /** Summed from the members: a stack has no tax context of its own to recalculate from. */
    totalWithTax: number | undefined;
}

/**
 * Sets the markup fields on a stacked group of billable shifts, pinning it to its markup total so
 * adjusting that one figure moves the client price by the same amount. The blended rate is hidden
 * on a stack, making the markup total the only figure a builder can meaningfully edit.
 *
 * The consolidated totals are taken as given and set on the row as-is; everything else on it —
 * `unitCost`, `markupAmount`, `markupPerUnit`, `markupPercent`, `margin` — is derived from them.
 *
 * The client price is preserved exactly: the markup is derived as the gap between it and the
 * builder cost. `unitCost` is rounded to the 4 decimals its column stores *before* the cost is
 * derived from it — it is a back-solved divide (11404.82 / 181.75 = 62.75004…), and a rate that
 * cannot reproduce its own cost would shift the client price by a cent the moment the row
 * recalculated. Rounding first makes the row consistent with the rate it displays and lets the
 * markup absorb the difference.
 *
 * The derived cost is rounded to cents *before* the markup is measured against it, so the two
 * 2 decimal figures sum back to the client price exactly. Measuring against the unrounded product
 * double-rounds: 45.50/hr over 169.25 hours derives a cost of 7700.875, whose half cent residue
 * rounds the markup up to 0.01 and then rounds the reassembled price up a second time, billing
 * 7700.89 against members summing to 7700.88. Holding the price exactly is also what keeps the
 * members' summed tax correct — it was summed alongside that price.
 */
function setBillableStackMarkupFields(
    stackedLineItem: OwnerInvoiceLineItem,
    consolidated: IConsolidatedStackTotals,
): void {
    const { quantity, builderCost, ownerPrice, totalWithTax } = consolidated;

    stackedLineItem.markupType = MarkupColumnTypes.Price;
    stackedLineItem.quantity = quantity;
    stackedLineItem.ownerPrice = ownerPrice;
    stackedLineItem.totalWithTax = totalWithTax;

    // Guarding the divide, not the rate: a zero quantity only reaches here if every shift in the
    // stack is zero length, and dividing by it throws.
    stackedLineItem.unitCost =
        quantity === 0 ? builderCost : round(divide(builderCost, quantity), 4);
    // recalculateAllFields derives builderCost as round(unitCost * quantity, 2), the same value the
    // markup is measured against here, so the two agree by construction.
    const derivedBuilderCost = round(multiply(stackedLineItem.unitCost, quantity), 2);
    stackedLineItem.markupAmount = round(subtract(ownerPrice, derivedBuilderCost), 2);

    const calculated = recalculateAllFields(stackedLineItem);

    stackedLineItem.builderCost = calculated.builderCost;
    stackedLineItem.markupAmount = calculated.markupAmount;
    stackedLineItem.markupPerUnit = calculated.markupPerUnit;
    stackedLineItem.markupPercent = calculated.markupPercent;
    // margin is optional on ICalculatedFields; the Price branch always returns one.
    stackedLineItem.margin = calculated.margin ?? 0;
}

/**
 * Sets the markup fields on a stacked group that is not billable time clock — bills, accounting
 * costs, and time clock on a job with no billable rates. Same contract as its billable sibling:
 * the consolidated totals are taken as given, everything else is derived from them.
 *
 * Unlike the billable stack this keeps the raw blended rate and an unrounded markup rather than
 * pinning the row to a markup total, which is the pre-existing behavior for these types.
 */
function setPercentageStackMarkupFields(
    stackedLineItem: OwnerInvoiceLineItem,
    consolidated: IConsolidatedStackTotals,
): void {
    const { quantity, builderCost, ownerPrice, totalWithTax } = consolidated;

    stackedLineItem.quantity = quantity;
    stackedLineItem.builderCost = builderCost;
    stackedLineItem.ownerPrice = ownerPrice;
    stackedLineItem.totalWithTax = totalWithTax;

    const markupAmount = subtract(ownerPrice, builderCost).toNumber();
    stackedLineItem.unitCost = quantity === 0 ? builderCost : builderCost / quantity;
    stackedLineItem.markupAmount = markupAmount;
    // quantity should never be zero here, but want to match logic in lineItemHelper.calculateMarkupFields
    stackedLineItem.markupPerUnit = quantity ? divide(markupAmount, quantity).toNumber() : 0;
    stackedLineItem.markupPercent = builderCost
        ? multiply(divide(markupAmount, builderCost), 100).toNumber()
        : 0;
}

export const mapNewLineItem = (
    mappedLineItem: LineItemForInvoice,
    isCostPlusWorkflow: boolean,
    shouldIncludeDescriptions: boolean,
    newLineItemId: number,
    taxGroup?: BTSelectItem<TaxGroupServiceListItemExtraData>,
    invoiceFormat?: InvoiceFormat,
    hasBillableRatesForJob: boolean = false,
    relatedEntityTagTypes: RelatedItemType[] = [],
): OwnerInvoiceLineItem => {
    const newLineItem = new OwnerInvoiceLineItem(
        newLineItemDefaults(
            newLineItemId,
            LineItemType.OwnerInvoice,
            0,
            mappedLineItem.costCode,
            false,
        ),
    );

    if (mappedLineItem.lineItemType === LineItemType.Bill && mappedLineItem.parentEntity) {
        newLineItem.relatedItem = {
            itemId: Number(mappedLineItem.parentEntity.id),
            itemType: mappedLineItem.lineItemType,
            itemLabel: mappedLineItem.parentEntity.title,
        };
    }

    // isCostPlusWorkflow translates to "from bills, timeclock, or accounting costs", which will need to be handled differently
    if (isCostPlusWorkflow) {
        newLineItem.unitCost = mappedLineItem.unitCost;
        newLineItem.quantity = mappedLineItem.quantity;
        newLineItem.builderCost =
            mappedLineItem.builderCost ??
            calculateBuilderCost(newLineItem.unitCost, newLineItem.quantity);
        // A billable shift is billed at a rate, so the line carries its markup per unit rather than
        // a percentage of cost — the only form that survives a shift with no configured labor cost.
        // Bills and accounting costs share this branch and keep their percentage.
        if (isBillableTimeClockLine(mappedLineItem, hasBillableRatesForJob)) {
            setBillableShiftMarkupFields(newLineItem, mappedLineItem.invoicedMarkupPerUnit ?? 0);
        } else {
            newLineItem.markupPercent = mappedLineItem.invoicedPercent!;
            recalculateAndSetMarkupFields(newLineItem);
        }
    } else {
        newLineItem.markupType = mappedLineItem.markupType;
        const percentage = divide(mappedLineItem.invoicedPercent!, 100);
        const unroundedQuantity = multiply(percentage, mappedLineItem.quantity).toNumber();
        newLineItem.quantity = round(unroundedQuantity, 4);

        newLineItem.unitCost = mappedLineItem.unitCost;
        newLineItem.builderCost = round(multiply(mappedLineItem.builderCost, percentage), 2);
        newLineItem.ownerPrice = mappedLineItem.invoicedAmount!;
        newLineItem.markupAmount = round(
            subtract(newLineItem.ownerPrice, newLineItem.builderCost).toNumber(),
            2,
        );
        newLineItem.markupPerUnit =
            unroundedQuantity !== 0 ? round(newLineItem.markupAmount / unroundedQuantity, 2) : 0;
        newLineItem.markupPercent = mappedLineItem.markupPercent;
        newLineItem.margin = mappedLineItem.margin;
        newLineItem.totalWithTax = newLineItem.ownerPrice;
    }

    newLineItem.costTypes = mappedLineItem.costTypes;
    newLineItem.itemTitle = mappedLineItem.title;
    newLineItem.unit = mappedLineItem.unitType;

    if (shouldIncludeDescriptions) {
        newLineItem.description = mappedLineItem.description;
        newLineItem.internalNotes = mappedLineItem.internalNotes;
    }

    newLineItem.newlyInvoicedAmount = mappedLineItem.invoicedAmount;
    const shouldApplyTax =
        (taxGroup?.value ?? NoTaxId) !== NoTaxId &&
        (invoiceFormat !== InvoiceFormat.ProgressInvoice || mappedLineItem.taxGroupId);
    if (shouldApplyTax) {
        if (mappedLineItem.taxGroupId) {
            newLineItem.taxGroupId = taxGroup?.value as number;
            newLineItem.totalWithTax = calculateTotalIncludingTax(
                newLineItem.ownerPrice,
                taxGroup?.extraData?.totalEffectiveRate ?? 0,
            );
        } else {
            newLineItem.totalWithTax = mappedLineItem.totalWithTax;
        }
    }

    switch (mappedLineItem.lineItemType) {
        case LineItemType.ChangeOrder:
            newLineItem.relatedChangeOrderLineItemId = mappedLineItem.lineItemId;
            break;
        case LineItemType.SelectionChoice:
        case LineItemType.SelectionOption:
            newLineItem.relatedSelectionChoiceLineItemId = mappedLineItem.lineItemId;
            // Carry the wizard allowance group id onto the row so the credit + selection lines added this
            // session group for whole-group delete. The credit line arrives as EstimateLineItem.
            newLineItem.selectionOptionGroupID = mappedLineItem.selectionOptionGroupID;
            // Carry the standalone-selection group key (SelectionOptionID) for the non-allowance grouped delete.
            // Only SRI selections carry a real (non-null) id; legacy SelectionChoice lines leave it null.
            newLineItem.selectionOptionID = mappedLineItem.selectionOptionID;
            break;
        case LineItemType.EstimateLineItem:
            newLineItem.relatedGeneralItemId = mappedLineItem.lineItemId;
            newLineItem.selectionOptionGroupID = mappedLineItem.selectionOptionGroupID;
            break;
        case LineItemType.Bid:
            newLineItem.relatedBidLineItemId = mappedLineItem.lineItemId;
            break;
        case LineItemType.Bill:
            newLineItem.relatedBillLineItemId = mappedLineItem.lineItemId;
            break;
        case LineItemType.Allowance:
            newLineItem.allowanceLineItemId = mappedLineItem.lineItemId;
            newLineItem.allowanceId = Number(mappedLineItem.parentEntity!.id);
            break;
        case LineItemType.TimeCardItem:
            newLineItem.relatedTimeCardLineItemId = mappedLineItem.lineItemId;
            newLineItem.relatedTimeCardItemId = Number(mappedLineItem.parentEntity!.id);
            break;
        case LineItemType.OtherAccountingCost:
            newLineItem.relatedAccountingCostId = mappedLineItem.lineItemId;
            break;
    }

    newLineItem.relatedItems = buildRelatedItemsFromLineItemForInvoice(
        mappedLineItem,
        relatedEntityTagTypes,
    );
    if (invoiceFormat === InvoiceFormat.ProgressInvoice) {
        createLineItemMetaData(newLineItem, mappedLineItem, taxGroup);
    }
    return newLineItem;
};

function createLineItemMetaData(
    lineItem: OwnerInvoiceLineItem,
    mappedLineItem: LineItemForInvoice,
    taxGroup?: BTSelectItem<TaxGroupServiceListItemExtraData>,
) {
    if (mappedLineItem.lineItemType === LineItemType.ChangeOrder) {
        createChangeOrderLineItemMetaData(lineItem, mappedLineItem, taxGroup);
    } else if (
        mappedLineItem.lineItemType === LineItemType.SelectionChoice ||
        mappedLineItem.lineItemType === LineItemType.SelectionOption ||
        mappedLineItem.lineItemType === LineItemType.EstimateLineItem
    ) {
        createSelectionLineItemMetaData(lineItem, mappedLineItem, taxGroup);
    } else {
        setCostsLineItemMetaData(lineItem, mappedLineItem);
    }
}

// On a progress invoice, "This period" (completed) tracks what's billed now while Revised budget
// (scheduledValue) carries the line's value being added. Selections and allowance overages come in
// billing 0 this period (the wizard zeroes invoicedAmount/invoicedPercent), so completed is 0 and
// the overage lives entirely in scheduledValue; the builder bills it manually. scheduledValue is
// re-derived from the source amount being added (amountRemaining) — the line's own ownerPrice may be
// 0 here — applying the PI tax rate the same way createChangeOrderLineItemMetaData does.
function createSelectionLineItemMetaData(
    lineItem: OwnerInvoiceLineItem,
    mappedLineItem: LineItemForInvoice,
    taxGroup?: BTSelectItem<TaxGroupServiceListItemExtraData>,
) {
    const totalEffectiveRate = taxGroup?.extraData?.totalEffectiveRate ?? 0;
    const piTaxAppliesToLine =
        lineItem.taxGroupId != null && lineItem.taxGroupId !== NoTaxId && totalEffectiveRate > 0;
    const scheduledValue = piTaxAppliesToLine
        ? calculateTotalIncludingTax(mappedLineItem.amountRemaining, totalEffectiveRate)
        : mappedLineItem.amountRemaining;
    const completed = lineItem.totalWithTax ?? lineItem.ownerPrice;

    lineItem.invoiceLineItemMetadata = {
        previousInvoiceAmount: 0,
        scheduledValue: scheduledValue,
        percentComplete: mappedLineItem.invoicedPercent,
        minimumPercentComplete: 0,
        completed: completed,
        balanceToFinish: subtract(scheduledValue, completed).toNumber(),
        relatedLineItemMetadata: {
            markupAmount: mappedLineItem.markupAmount,
            builderCost: mappedLineItem.builderCost,
            quantity: mappedLineItem.quantity,
        },
    };

    // Unbudgeted selection lines (allowance overage, or standalone post-budget selections) roll into
    // the "Approved changes" / "Revised scheduled value" columns. Tag them by their parent selection entity
    // (mirroring how change-order lines are tagged above) so the estimate-mode Approved changes
    // section can group a selection's line items into one row and link to the selection. Cost-code
    // mode aggregates by isApprovedChangeLine and ignores groupId, so it is unaffected. An unbudgeted
    // selection is always a standalone selection or a selection-choice line under its parent
    // selection, so parentEntity is always present — never fall back to the cost code.
    if (mappedLineItem.isUnbudgetedSelection) {
        lineItem.invoiceLineItemMetadata.groupingData = {
            groupType: InvoiceLineItemGroupType.UnbudgetedSelection,
            groupId: Number(mappedLineItem.parentEntity!.id),
            groupTitle: mappedLineItem.parentEntity!.title ?? null,
        };
    }
}

function setCostsLineItemMetaData(
    lineItem: OwnerInvoiceLineItem,
    mappedLineItem: LineItemForInvoice,
) {
    if (lineItem.invoiceLineItemMetadata) {
        lineItem.invoiceLineItemMetadata = {
            ...lineItem.invoiceLineItemMetadata,
            completed:
                (lineItem.invoiceLineItemMetadata.previousInvoiceAmount ?? 0) +
                (lineItem.totalWithTax ?? lineItem.ownerPrice),
        };
    } else {
        lineItem.invoiceLineItemMetadata = {
            previousInvoiceAmount: 0,
            completed: lineItem.totalWithTax ?? lineItem.ownerPrice,
            relatedLineItemMetadata: {
                markupAmount: mappedLineItem.markupAmount,
                builderCost: mappedLineItem.builderCost,
                quantity: mappedLineItem.quantity,
            },
        };
    }
}

function createChangeOrderLineItemMetaData(
    lineItem: OwnerInvoiceLineItem,
    mappedLineItem: LineItemForInvoice,
    taxGroup?: BTSelectItem<TaxGroupServiceListItemExtraData>,
) {
    // scheduledValue (Budget) is the full source CO line total contributed to the PI's
    // schedule of values. The source CO may carry its own per-line tax group different
    // from the PI's rate, so re-derive from the source's pre-tax ownerPrice with the
    // PI's rate applied. completed (This Invoice) uses the post-tax lineItem total so
    // Budget and This Invoice stay consistent in the PI grid.
    const totalEffectiveRate = taxGroup?.extraData?.totalEffectiveRate ?? 0;
    const piTaxAppliesToLine =
        lineItem.taxGroupId != null && lineItem.taxGroupId !== NoTaxId && totalEffectiveRate > 0;
    const scheduledValue = piTaxAppliesToLine
        ? calculateTotalIncludingTax(mappedLineItem.ownerPrice, totalEffectiveRate)
        : mappedLineItem.ownerPrice;
    const completed = lineItem.totalWithTax ?? lineItem.ownerPrice;

    lineItem.invoiceLineItemMetadata = {
        previousInvoiceAmount: 0,
        scheduledValue: scheduledValue,
        percentComplete: mappedLineItem.invoicedPercent,
        minimumPercentComplete: 0,
        completed: completed,
        balanceToFinish: subtract(scheduledValue, completed).toNumber(),
        relatedLineItemMetadata: {
            markupAmount: mappedLineItem.markupAmount,
            builderCost: mappedLineItem.builderCost,
            quantity: mappedLineItem.quantity,
        },
        groupingData: {
            groupType: InvoiceLineItemGroupType.ChangeOrder,
            groupId: Number(mappedLineItem.parentEntity!.id),
            groupTitle: mappedLineItem.parentEntity!.title ?? null,
        },
    };
}

// Recomputes completed/percentComplete/balanceToFinish against the existing scheduledValue
// when merging a wizard row into an already-on-invoice line. The merged pre-tax ownerPrice is
// re-totalled at the PI's rate — the source entity may carry its own, different rate — then
// completed is derived from that total, matching how the backend recomputes it.
function updateRelatedLineItemMetaData(
    relatedLineItem: IOwnerInvoiceLineItem,
    mappedLineItem: LineItemForInvoice,
    taxGroup?: BTSelectItem<TaxGroupServiceListItemExtraData>,
) {
    const totalEffectiveRate = taxGroup?.extraData?.totalEffectiveRate ?? 0;
    const piTaxAppliesToLine =
        relatedLineItem.taxGroupId != null &&
        relatedLineItem.taxGroupId !== NoTaxId &&
        totalEffectiveRate > 0;
    relatedLineItem.totalWithTax = piTaxAppliesToLine
        ? calculateTotalIncludingTax(relatedLineItem.ownerPrice, totalEffectiveRate)
        : relatedLineItem.ownerPrice;

    const scheduledValue =
        relatedLineItem.invoiceLineItemMetadata?.scheduledValue ??
        mappedLineItem.totalWithTax ??
        mappedLineItem.ownerPrice;
    const completed = add(
        relatedLineItem.invoiceLineItemMetadata?.previousInvoiceAmount ?? 0,
        relatedLineItem.totalWithTax ?? relatedLineItem.ownerPrice,
    )
        .add(relatedLineItem.storedMaterials ?? 0)
        .toNumber();
    const percentComplete =
        scheduledValue !== 0 ? multiply(divide(completed, scheduledValue), 100).toNumber() : 0;
    relatedLineItem.invoiceLineItemMetadata = {
        ...relatedLineItem.invoiceLineItemMetadata,
        percentComplete: percentComplete,
        completed: completed,
        balanceToFinish: subtract(scheduledValue, completed).toNumber(),
    };
}

// for invoicing bills, these markup fields do not exist so the wizard will need to recalculate the full line item based on invoice %
export function recalculateAndSetMarkupFieldsForLineItemForInvoice(
    lineItem: LineItemForInvoice,
    totalEffectiveRate?: number,
    taxMethod?: number,
) {
    const ownerInvoiceLineItem = new OwnerInvoiceLineItem(lineItem);
    ownerInvoiceLineItem.markupType = MarkupColumnTypes.Percentage;
    ownerInvoiceLineItem.markupPercent = lineItem.invoicedPercent!;

    recalculateAndSetMarkupFields(ownerInvoiceLineItem, totalEffectiveRate, taxMethod);

    lineItem.markupAmount = ownerInvoiceLineItem.markupAmount;
    lineItem.markupPerUnit = ownerInvoiceLineItem.markupPerUnit;
    lineItem.markupPercent = ownerInvoiceLineItem.markupPercent;
    lineItem.markupType = MarkupColumnTypes.Percentage;
    lineItem.ownerPrice = ownerInvoiceLineItem.ownerPrice;
    lineItem.totalWithTax = ownerInvoiceLineItem.totalWithTax;
}

export function recalculateBuilderCostMarkupAndOwnerPrice(
    relatedLineItem: IOwnerInvoiceLineItem,
    x: LineItemForInvoice,
) {
    relatedLineItem.ownerPrice = add(relatedLineItem.ownerPrice, x.invoicedAmount!).toNumber();

    const percentage = divide(relatedLineItem.ownerPrice, x.ownerPrice);
    const unroundedQuantity = multiply(percentage, x.quantity).toNumber();

    relatedLineItem.quantity = round(unroundedQuantity, 4);
    relatedLineItem.unitCost = x.unitCost;
    relatedLineItem.builderCost = round(multiply(x.builderCost, percentage), 2);

    relatedLineItem.markupAmount = subtract(
        relatedLineItem.ownerPrice,
        relatedLineItem.builderCost,
    ).toNumber();
    relatedLineItem.markupPercent = x.markupPercent;
    relatedLineItem.markupPerUnit = round(
        divide(relatedLineItem.markupAmount, unroundedQuantity),
        2,
    );
    relatedLineItem.totalWithTax = round(
        multiply(x.totalWithTax ?? relatedLineItem.ownerPrice, percentage),
        4,
    );
    relatedLineItem.margin = x.margin;
}

export function getExistingRelatedLineItem(
    existingLineItems: IOwnerInvoiceLineItem[],
    lineItemType: LineItemType,
    lineItemId: number,
) {
    switch (lineItemType) {
        case LineItemType.ChangeOrder:
            return existingLineItems.find((x) => x.relatedChangeOrderLineItemId === lineItemId);
        case LineItemType.SelectionChoice:
        case LineItemType.SelectionOption:
            return existingLineItems.find((x) => x.relatedSelectionChoiceLineItemId === lineItemId);
        case LineItemType.EstimateLineItem:
            return existingLineItems.find((x) => x.relatedGeneralItemId === lineItemId);
        case LineItemType.Bid:
            return existingLineItems.find((x) => x.relatedBidLineItemId === lineItemId);
        case LineItemType.Bill:
            return existingLineItems.find((x) => x.relatedBillLineItemId === lineItemId);
        case LineItemType.Allowance:
            return existingLineItems.find((x) => x.allowanceLineItemId === lineItemId);
    }
    return null;
}

export function getNewInvoicedAmount(
    relatedLineItem: IOwnerInvoiceLineItem,
    x: LineItemForInvoice,
) {
    if (relatedLineItem.newlyInvoicedAmount && x.invoicedAmount) {
        return add(relatedLineItem.newlyInvoicedAmount, x.invoicedAmount).toNumber();
    } else {
        return x.invoicedAmount;
    }
}

export function recalculateAddLineItemsToInvoice(
    lineItems: LineItemForInvoice[],
    updatedLineItems: IOwnerInvoiceLineItem[],
    newLineItemId: number,
    includeDescriptions: boolean,
    effectiveTaxVersion: JobTaxVersion,
    isOptedOutOfTaxes: boolean,
    invoiceFormat: InvoiceFormat,
    taxGroup?: BTSelectItem<TaxGroupServiceListItemExtraData>,
    hasBillableRatesForJob: boolean = false,
    relatedEntityTagTypes: RelatedItemType[] = [],
): [OwnerInvoiceLineItem[], number] {
    const newLineItems: OwnerInvoiceLineItem[] = [];
    lineItems.forEach((x) => {
        const shouldRespectEntityTaxStatus = shouldRespectEntityTaxableStatus(
            x.lineItemType,
            effectiveTaxVersion,
        );

        // Skip the invoice-level tax-group override for bid-derived bills — the backend already stamps the
        // correct taxable status from the source bid.
        const isBidDerivedBill =
            x.lineItemType === LineItemType.Bill && x.relatedBidLineItemId != null;

        if (isOptedOutOfTaxes) {
            x.taxGroupId = undefined;
        } else if (!shouldRespectEntityTaxStatus && !isBidDerivedBill) {
            x.taxGroupId = taxGroup?.value as number | undefined;
        }
        const relatedLineItem = getExistingRelatedLineItem(
            updatedLineItems,
            x.lineItemType,
            x.lineItemId,
        );

        if (relatedLineItem) {
            // line item related to same line item already exists, update its values instead of making a new line item
            recalculateBuilderCostMarkupAndOwnerPrice(relatedLineItem, x);
            relatedLineItem.newlyInvoicedAmount = getNewInvoicedAmount(relatedLineItem, x);
            if (!relatedLineItem.relatedItems?.length) {
                relatedLineItem.relatedItems = buildRelatedItemsFromLineItemForInvoice(
                    x,
                    relatedEntityTagTypes,
                );
            }
            if (includeDescriptions) {
                // if the existing line item does not have a description or internal notes, update it.
                if (!relatedLineItem.description?.trim()) {
                    relatedLineItem.description = x.description;
                }
                if (!relatedLineItem.internalNotes?.trim()) {
                    relatedLineItem.internalNotes = x.internalNotes;
                }
            }

            if (shouldRespectEntityTaxStatus) {
                relatedLineItem.taxGroupId = setExistingLineItemTaxGroupId(
                    x.taxGroupId,
                    taxGroup?.value as number | undefined,
                );
            }

            if (
                invoiceFormat === InvoiceFormat.ProgressInvoice &&
                relatedLineItem.invoiceLineItemMetadata &&
                (x.lineItemType === LineItemType.ChangeOrder ||
                    x.lineItemType === LineItemType.SelectionChoice ||
                    x.lineItemType === LineItemType.SelectionOption ||
                    x.lineItemType === LineItemType.EstimateLineItem)
            ) {
                updateRelatedLineItemMetaData(relatedLineItem, x, taxGroup);
            }
        } else {
            const newLineItem = mapNewLineItem(
                x,
                x.isCostPlusWorkflow,
                includeDescriptions,
                newLineItemId--,
                taxGroup,
                invoiceFormat,
                hasBillableRatesForJob,
                relatedEntityTagTypes,
            );
            newLineItems.push(newLineItem);
        }
    });

    return [newLineItems, newLineItemId];
}

export function mapRelatedEntityLineItemsToInvoiceLineItems(
    newLineItems: LineItemForInvoice[],
    existingLineItems: IOwnerInvoiceLineItem[],
    nextLineItemId: number,
    includeDescriptions: boolean,
    supportsLineItemStacking: boolean,
    stackLineItems: boolean,
    taxGroup?: BTSelectItem<TaxGroupServiceListItemExtraData>,
    effectiveTaxVersion?: JobTaxVersion,
    invoiceFormat?: InvoiceFormat,
    isOptedOutOfTaxes: boolean = false,
    hasBillableRatesForJob: boolean = false,
    showTimeCardTags: boolean = false,
    relatedEntityTagTypes: RelatedItemType[] = [],
): [OwnerInvoiceLineItem[], number] {
    let [individualLineItems, newLineItemId] = recalculateAddLineItemsToInvoice(
        newLineItems,
        existingLineItems,
        nextLineItemId,
        includeDescriptions,
        effectiveTaxVersion ?? JobTaxVersion.None,
        isOptedOutOfTaxes,
        invoiceFormat ?? InvoiceFormat.Invoice,
        taxGroup,
        hasBillableRatesForJob,
        relatedEntityTagTypes,
    );
    let lineItemsFromRelatedEntity: OwnerInvoiceLineItem[] = individualLineItems;
    if (supportsLineItemStacking) {
        newLineItemId = nextLineItemId; // reset next line item id to what was passed in
        const stackedLineItems: OwnerInvoiceLineItem[] = [];

        // Read the type off the source rows: the mapped OwnerInvoiceLineItems hardcode
        // lineItemType to OwnerInvoice. The caller invokes this once per entity type, so this
        // whole call is either time clock or it isn't — `every` keeps the percentage behavior if
        // that ever stops holding.
        const isBillableTimeClockInvoicing =
            newLineItems.length > 0 &&
            newLineItems.every((li) => isBillableTimeClockLine(li, hasBillableRatesForJob));

        if (stackLineItems) {
            // group line items by cost code
            const costCodeLineItemMap = individualLineItems.reduce(
                (map, lineItem) =>
                    map.set(lineItem.costCodeId!, [
                        ...(map.get(lineItem.costCodeId!) ?? []),
                        lineItem,
                    ]),
                new Map<number, OwnerInvoiceLineItem[]>(),
            );

            costCodeLineItemMap.forEach((sharedCostCodeLineItems: OwnerInvoiceLineItem[]) => {
                const entityIdLineItemIds: Record<number, RelatedStackItem[]> = {};
                sharedCostCodeLineItems.forEach((li) =>
                    mapRelatedItemIdsForStack(li, newLineItems, entityIdLineItemIds),
                );

                // add invoice line item with stacks for each line item grouped by cost code
                const stackedLineItemId = newLineItemId--;
                const stackedLineItem = new OwnerInvoiceLineItem({
                    ...sharedCostCodeLineItems[0], // use non-calculated values from the first line item
                    lineItemId: stackedLineItemId,
                    itemTitle: getStackedLineItemTitle(newLineItems, sharedCostCodeLineItems),
                    unit: "Hours",
                    relatedStackLinks: entityIdLineItemIds,
                    relatedItems: showTimeCardTags
                        ? buildTimeCardRelatedItemsFromStackLinks(entityIdLineItemIds)
                        : undefined,
                    relatedTimeCardItemId: null,
                    relatedTimeCardLineItemId: null,
                    invoiceLineItemMetadata: createMetaDataForStackedLineItems(
                        sharedCostCodeLineItems,
                        stackedLineItemId,
                    ),
                });

                // A cost code selected only once is a "group" of one with nothing to consolidate, so
                // the member's own cost fields carry through untouched. Consolidating them anyway
                // back-solves unitCost as builderCost / quantity — and builderCost is a 2dp
                // projection of unitCost * quantity, so the rate cannot be recovered and the error is
                // amplified by 1/quantity (a 15 minute shift at $45.50 comes back as $45.52).
                if (sharedCostCodeLineItems.length > 1) {
                    const summedBuilderCost = sumListValue(sharedCostCodeLineItems, "builderCost");
                    const summedOwnerPrice = sumListValue(sharedCostCodeLineItems, "ownerPrice");
                    // Summed as a decimal, not with `+`: the quantity column stores 4 decimals, and
                    // in float 0.1 + 0.2 is 0.30000000000000004 — a value the row cannot store, fed
                    // into the rate divide and the cost derived from it.
                    const quantity = sumListValue(sharedCostCodeLineItems, "quantity");
                    const summedTotalWithTax =
                        sumNullable(sharedCostCodeLineItems.map((x) => x.totalWithTax ?? null)) ??
                        undefined;
                    const consolidated: IConsolidatedStackTotals = {
                        quantity,
                        builderCost: summedBuilderCost,
                        ownerPrice: summedOwnerPrice,
                        totalWithTax: summedTotalWithTax,
                    };

                    if (isBillableTimeClockInvoicing) {
                        // The members' rates are blended away in the sums above, so the line no
                        // longer has a rate to be pinned to. Pin it to the markup total instead, so
                        // adjusting that one figure moves the client price by the same amount. A
                        // $/unit stack would recalculate from a blended rate, and a percentage one
                        // bills nothing at all when the shifts carry a bill rate but no labor cost.
                        setBillableStackMarkupFields(stackedLineItem, consolidated);
                    } else {
                        setPercentageStackMarkupFields(stackedLineItem, consolidated);
                    }
                }

                stackedLineItem.newlyInvoicedAmount = stackedLineItem.ownerPrice;
                stackedLineItems.push(stackedLineItem);
            });
        } else {
            // add invoice line item with stack for every line item
            individualLineItems.forEach((li) => {
                const entityIdLineItemIds: Record<number, RelatedStackItem[]> = {};
                mapRelatedItemIdsForStack(li, newLineItems, entityIdLineItemIds);
                const stackedLineItem = new OwnerInvoiceLineItem({
                    ...li,
                    lineItemId: newLineItemId--,
                    unit: "Hours",
                    relatedStackLinks: entityIdLineItemIds,
                    relatedItems: showTimeCardTags
                        ? buildTimeCardRelatedItemsFromStackLinks(entityIdLineItemIds)
                        : undefined,
                    relatedTimeCardItemId: null,
                    relatedTimeCardLineItemId: null,
                });
                stackedLineItem.newlyInvoicedAmount = li.ownerPrice;
                stackedLineItems.push(stackedLineItem);
            });
        }

        lineItemsFromRelatedEntity = stackedLineItems;
    }

    return [lineItemsFromRelatedEntity, newLineItemId];
}

function createMetaDataForStackedLineItems(
    lineItems: OwnerInvoiceLineItem[],
    newLineItemId: number,
): InvoiceLineItemMetadata | undefined {
    return lineItems
        .map((x) => x.invoiceLineItemMetadata)
        .reduce((acc, curr) => {
            return {
                invoiceLineItemId: newLineItemId,
                previousInvoiceAmount:
                    (acc?.previousInvoiceAmount ?? 0) + (curr?.previousInvoiceAmount ?? 0),
                completed: (acc?.completed ?? 0) + (curr?.completed ?? 0),
                relatedLineItemMetadata: {
                    markupAmount:
                        (acc?.relatedLineItemMetadata?.markupAmount ?? 0) +
                        (curr?.relatedLineItemMetadata?.markupAmount ?? 0),
                    builderCost:
                        (acc?.relatedLineItemMetadata?.builderCost ?? 0) +
                        (curr?.relatedLineItemMetadata?.builderCost ?? 0),
                    quantity:
                        (acc?.relatedLineItemMetadata?.quantity ?? 0) +
                        (curr?.relatedLineItemMetadata?.quantity ?? 0),
                },
            };
        }, undefined);
}

export function mapRelatedEntityLineItemsFormToInvoiceLineItems(
    lineItemsToInvoiceFormValues: ILineItemsToInvoiceFormValues,
    existingLineItems: IOwnerInvoiceLineItem[],
    nextLineItemId: number,
    includeDescriptions: boolean,
    taxGroup?: BTSelectItem<TaxGroupServiceListItemExtraData>,
    effectiveTaxVersion?: JobTaxVersion,
): [OwnerInvoiceLineItem[], number] {
    return mapRelatedEntityLineItemsToInvoiceLineItems(
        lineItemsToInvoiceFormValues.lineItems,
        existingLineItems,
        nextLineItemId,
        includeDescriptions,
        lineItemsToInvoiceFormValues.supportsLineItemStacking,
        lineItemsToInvoiceFormValues.stackLineItems,
        taxGroup,
        effectiveTaxVersion,
    );
}

/**
 * Private helper function that converts dates to builder's timezone and returns values needed for validation
 */
export function getBuilderTimezoneDates(date: Date, timeLocale: TimeLocale) {
    const timezoneName = timeLocale.timeZoneName;
    // now from the user's computer
    const now = new Date();

    const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: timezoneName,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    });

    // converting user's computer time to builder's time
    const nowInBuilderTimeString = formatter.format(now);
    const nowInBuilderTimeDate = new Date(nowInBuilderTimeString);
    // get today's date in builder's time
    const today = new Date(
        nowInBuilderTimeDate.getFullYear(),
        nowInBuilderTimeDate.getMonth(),
        nowInBuilderTimeDate.getDate(),
    );
    const dayOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const threeMonthsFromNow = new Date(
        nowInBuilderTimeDate.getFullYear(),
        nowInBuilderTimeDate.getMonth() + 3,
        nowInBuilderTimeDate.getDate(),
    );

    return { today, dayOfDate, threeMonthsFromNow };
}

/**
 * Validates schedule dates - must be tomorrow or later, within 3 months, and before/equal to due date if provided
 */
export function isScheduleDateInvalid(
    date: Date,
    timeLocale: TimeLocale,
    invoiceDueDate?: Date,
): boolean {
    const { today, dayOfDate, threeMonthsFromNow } = getBuilderTimezoneDates(date, timeLocale);

    // Schedule date must be:
    // 1. Tomorrow or later (from builder's time perspective)
    // 2. Within 3 months of today
    // 3. Before or equal to due date (if due date exists)
    return (
        dayOfDate <= today ||
        date > threeMonthsFromNow ||
        (invoiceDueDate !== undefined && dayOfDate > invoiceDueDate)
    );
}

/**
 * Validates due dates - must be on or after invoice date and within 90 days of invoice date
 */
export function isDueDateInvalid(date: Date, timeLocale: TimeLocale, invoiceDate: Date): boolean {
    const { dayOfDate } = getBuilderTimezoneDates(date, timeLocale);

    // Ensure invoiceDate is a Date object
    const invoiceDateObj = invoiceDate instanceof Date ? invoiceDate : new Date(invoiceDate);

    // Normalize invoice date to just year/month/day for comparison
    const dayOfInvoiceDate = new Date(
        invoiceDateObj.getFullYear(),
        invoiceDateObj.getMonth(),
        invoiceDateObj.getDate(),
    );

    // Calculate 90 days from invoice date
    const ninetyDaysFromInvoiceDate = new Date(dayOfInvoiceDate);
    ninetyDaysFromInvoiceDate.setDate(dayOfInvoiceDate.getDate() + 90);

    // Due date must be:
    // 1. On or after invoice date (same day is allowed)
    // 2. Within 90 days of invoice date (inclusive - day 90 is valid)
    // Note: No "tomorrow or later" check for due dates
    return dayOfDate < dayOfInvoiceDate || dayOfDate > ninetyDaysFromInvoiceDate;
}

function getStackedLineItemTitle(
    allLineItems: LineItemForInvoice[],
    mappedItemsByCostCode: OwnerInvoiceLineItem[],
) {
    if (
        mappedItemsByCostCode.length === 1 || // if there is only one line item to the cost code, use its title
        allLineItems[0].lineItemType !== LineItemType.TimeCardItem // time clock only entity that supports stacking for now
    ) {
        return mappedItemsByCostCode[0].itemTitle;
    }

    // get the line items to invoice that share the same cost code
    const lineItemsWithCostCode = allLineItems.filter(
        (li) => li.costCode === mappedItemsByCostCode[0].costCodeId,
    );
    const firstLineItem = lineItemsWithCostCode[0];
    const lastLineItem = lineItemsWithCostCode[lineItemsWithCostCode.length - 1];
    switch (firstLineItem.lineItemType) {
        case LineItemType.TimeCardItem:
            return `Labor - ${lastLineItem.dateToFilter?.format(
                "MM/DD/YY",
            )} to ${firstLineItem.dateToFilter?.format("MM/DD/YY")}`;
        default:
            return firstLineItem.title;
    }
}

function mapRelatedItemIdsForStack(
    li: OwnerInvoiceLineItem,
    newLineItems: LineItemForInvoice[],
    entityIdLineItemIds: Record<number, RelatedStackItem[]>,
) {
    if (entityIdLineItemIds[li.relatedTimeCardItemId!] === undefined) {
        entityIdLineItemIds[li.relatedTimeCardItemId!] = [];
    }

    const lineItem = newLineItems.find(
        (x) =>
            x.parentEntity &&
            x.parentEntity.id === li.relatedTimeCardItemId! &&
            x.lineItemId === li.relatedTimeCardLineItemId!,
    );
    entityIdLineItemIds[li.relatedTimeCardItemId!].push(
        new RelatedStackItem(
            li.relatedTimeCardLineItemId!,
            lineItem!.parentEntity!.title,
            lineItem!.lineItemType,
        ),
    );
}

export function shouldRespectEntityTaxableStatus(
    lineItemType: LineItemType,
    effectiveTaxVersion: JobTaxVersion,
): boolean {
    if (effectiveTaxVersion < JobTaxVersion.V1) {
        return false;
    }

    switch (lineItemType) {
        case LineItemType.EstimateLineItem:
        case LineItemType.ChangeOrder:
        case LineItemType.SelectionOption:
        case LineItemType.Bid:
            return true;
        default:
            return false;
    }
}

export function setExistingLineItemTaxGroupId(
    incomingLineTaxGroupId?: number | null,
    invoiceTaxGroupId?: number,
): number {
    // Invoice has no tax group set, keep existing line non-taxable
    if (
        invoiceTaxGroupId === NoTaxId ||
        invoiceTaxGroupId === undefined ||
        invoiceTaxGroupId === null
    ) {
        return NoTaxId;
    }

    // If incoming line is non-taxable, set existing line to non-taxable
    if (
        incomingLineTaxGroupId === NoTaxId ||
        incomingLineTaxGroupId === undefined ||
        incomingLineTaxGroupId === null
    ) {
        return NoTaxId;
    }

    // Incoming line is taxable, set existing line to taxable with invoice tax group
    return invoiceTaxGroupId;
}

/**
 * Builds the single line item a flat-fee invoice is persisted as, from live form values.
 *
 * `FinalizeFlatFeeLineItem` (InvoiceManager.cs) normalises flat fee to exactly one row —
 * `ownerPrice` = the invoice amount, `taxGroupId` = the invoice-level group — and reads tax back
 * off it. Mirroring that row keeps flat fee on the shared line-item path instead of a parallel
 * calculation. It can't read the persisted row: a new invoice has none, and the amount field only
 * syncs to `ownerPrice` on save.
 */
export const buildFlatFeePreviewLineItem = (
    existingLineItem: IOwnerInvoiceLineItem | undefined,
    amount: number,
    taxGroupId: number,
    taxMethod: TaxMethod | undefined,
    taxGroups: BTSelectItem<TaxGroupServiceListItemExtraData>[] | undefined,
): IOwnerInvoiceLineItem => {
    const isTaxable = taxGroupId !== NoTaxId && !!taxGroups && taxGroups.length > 0;
    const flatFeeTax =
        isTaxable && taxMethod !== undefined
            ? getTotalTaxesOnEntity([], true, true, taxGroups, taxMethod, taxGroupId, amount)
            : 0;

    return {
        ...existingLineItem,
        ownerPrice: amount,
        taxGroupId,
        // calculateLineItemInvoiceTotals treats `totalWithTax ?? ownerPrice` as tax-inclusive and
        // backs tax out of it, so this must carry tax — inheriting or clearing it understates the
        // total. Inclusive amounts already include it.
        totalWithTax: taxMethod === TaxMethod.Exclusive ? amount + flatFeeTax : amount,
    } as IOwnerInvoiceLineItem;
};

export interface IInvoiceTotalAmounts {
    baseInvoiceAmount: number;
    storedMaterials: number;
    retainageHeld: number;
    retainageReleased: number;
    tax: number;
    subtotal: number;
    totalInvoiceAmount: number;
}

/**
 * Sums the invoice-level amounts (tax, stored materials, retainage, totals) from a set of line
 * items. Two known inaccuracies, tracked for a fix here:
 *
 * - `baseInvoiceAmount` (and the `subtotal`/`totalInvoiceAmount` derived from it) understates by
 *   the tax amount on lines with no `totalWithTax` — the `ownerPrice` fallback is pre-tax.
 *   `mapLineItem`/`recalculateLineItemTax` always populate it; ad-hoc lines may not.
 * - `storedMaterials` nets out tax under both methods, which is only correct under Exclusive.
 *
 * To agree with a surface rendering per-line owner prices, sum `ownerPrice` plus
 * `getStoredMaterialsThisPeriod` instead — see the Client preview in `OwnerInvoicePresentational`.
 */
export const calculateLineItemInvoiceTotals = (
    lineItems: IOwnerInvoiceLineItem[],
    taxGroups: BTSelectItem<TaxGroupServiceListItemExtraData>[] | undefined,
    taxMethod: TaxMethod | undefined,
): IInvoiceTotalAmounts => {
    // Decimal throughout, for the same half-cent reason as `calculateTotalPrice`.
    let invoiceAmountWithTax = new Decimal(0);
    let totalStoredMaterialsThisPeriod = new Decimal(0);
    let storedMaterialsTaxThisPeriod = new Decimal(0);
    let retainageHeld = new Decimal(0);
    let retainageReleased = new Decimal(0);

    for (const li of lineItems) {
        const meta = li.invoiceLineItemMetadata;
        invoiceAmountWithTax = add(invoiceAmountWithTax, li.totalWithTax ?? li.ownerPrice ?? 0);
        // Unlike "this invoice" amounts, stored materials and retainage default to the previous invoice's value, so subtract the previous
        // amount for the "this period" total. "storedMaterials" is tax-inclusive.
        totalStoredMaterialsThisPeriod = add(
            totalStoredMaterialsThisPeriod,
            subtract(li.storedMaterials ?? 0, meta?.previousStoredMaterials ?? 0),
        );
        storedMaterialsTaxThisPeriod = add(
            storedMaterialsTaxThisPeriod,
            meta?.storedMaterialsTax ?? 0,
        );
        const changeInRetainage = subtract(li.retainageWithheld ?? 0, meta?.previousRetainage ?? 0);
        if (changeInRetainage.gt(0)) {
            retainageHeld = add(retainageHeld, changeInRetainage);
        } else if (changeInRetainage.lt(0)) {
            retainageReleased = add(retainageReleased, changeInRetainage.neg());
        }
    }

    const workTax =
        taxGroups && taxGroups.length > 0 && taxMethod !== undefined
            ? getTotalTaxesOnEntity(lineItems, false, true, taxGroups, taxMethod, 0, 0)
            : 0;
    const tax = add(workTax, storedMaterialsTaxThisPeriod);
    const baseInvoiceAmount = subtract(invoiceAmountWithTax, workTax);
    const storedMaterials = subtract(totalStoredMaterialsThisPeriod, storedMaterialsTaxThisPeriod);

    return {
        baseInvoiceAmount: baseInvoiceAmount.toNumber(),
        storedMaterials: storedMaterials.toNumber(),
        retainageHeld: retainageHeld.toNumber(),
        retainageReleased: retainageReleased.toNumber(),
        tax: tax.toNumber(),
        subtotal: sum([baseInvoiceAmount, storedMaterials, tax]).toNumber(),
        totalInvoiceAmount: sum([
            baseInvoiceAmount,
            storedMaterials,
            retainageHeld.neg(),
            retainageReleased,
            tax,
        ]).toNumber(),
    };
};

export interface IClientPreviewTotals {
    /** Line items to render, with flat fee normalised to the single row it persists as. */
    lineItems: IOwnerInvoiceLineItem[];
    subtotal: number;
    /** Subtotal plus Exclusive tax, net of retainage. */
    invoiceAmount: number;
    /**
     * Present on this invoice, so the matching toggle is locked on — hiding either would leave the
     * coversheet unreconcilable.
     */
    hasRetainage: boolean;
    hasStoredMaterials: boolean;
}

/**
 * Totals for the Client preview, derived the way the Details tab derives them: by summing the
 * stored per-line `ownerPrice`, so the two tabs can't disagree. `calculateLineItemInvoiceTotals`
 * is used only for tax and retainage — its `baseInvoiceAmount` and `storedMaterials` carry the
 * caveats documented on it, and `getStoredMaterialsThisPeriod` is what `InvoicePreviewBody` uses.
 */
export const calculateClientPreviewTotals = (
    values: Pick<
        IOwnerInvoiceFormValues,
        "priceType" | "ownerInvoiceLineItems" | "amount" | "taxGroupId" | "taxMethod"
    >,
    taxGroups: BTSelectItem<TaxGroupServiceListItemExtraData>[] | undefined,
    invoiceTaxGroups: BTSelectItem<TaxGroupServiceListItemExtraData>[] | undefined,
): IClientPreviewTotals => {
    const lineItems =
        values.priceType === PriceTypes.FlatFee
            ? [
                  buildFlatFeePreviewLineItem(
                      values.ownerInvoiceLineItems[0],
                      values.amount ?? 0,
                      values.taxGroupId ?? NoTaxId,
                      values.taxMethod,
                      taxGroups,
                  ),
              ]
            : values.ownerInvoiceLineItems;

    const totals = calculateLineItemInvoiceTotals(lineItems, invoiceTaxGroups, values.taxMethod);
    const subtotalDecimal = sum(
        lineItems.map((li) =>
            add(li.ownerPrice ?? 0, getStoredMaterialsThisPeriod(li, values.taxMethod)),
        ),
    );
    const invoiceAmount = add(
        subtract(
            add(subtotalDecimal, values.taxMethod === TaxMethod.Exclusive ? totals.tax : 0),
            totals.retainageHeld,
        ),
        totals.retainageReleased,
    ).toNumber();

    return {
        lineItems,
        subtotal: subtotalDecimal.toNumber(),
        invoiceAmount,
        hasRetainage: totals.retainageHeld > 0 || totals.retainageReleased > 0,
        hasStoredMaterials: totals.storedMaterials !== 0,
    };
};

interface IBillOrderingInfo {
    /** Index of the first cost code the related entity has a matching line item for. */
    costCodePosition: number;
    /** First index of the related entity, which can appear across multiple cost codes. */
    selectionOrder: number;
}

/**
 * For each entity referenced by the invoice line items, sets the ordering based on how it will appear on the line items table (ordered by cost code)
 */
const buildBillOrderingInfo = (lineItems: LineItemForInvoice[]): Map<number, IBillOrderingInfo> => {
    const costCodePositions = new Map<number, number>();
    lineItems.forEach((li, index) => {
        if (li.costCode != null && !costCodePositions.has(li.costCode)) {
            costCodePositions.set(li.costCode, index);
        }
    });
    const billInfo = new Map<number, IBillOrderingInfo>();
    lineItems.forEach((li, index) => {
        // only supports add from bills for now
        if (li.lineItemType !== LineItemType.Bill) {
            return;
        }
        const billId = Number(li.parentEntity?.id);
        if (!billId) {
            return;
        }
        const costCodePosition = costCodePositions.get(li.costCode) ?? Number.MAX_SAFE_INTEGER;
        const existing = billInfo.get(billId);
        if (existing === undefined) {
            billInfo.set(billId, {
                costCodePosition,
                selectionOrder: index,
            });
        } else if (costCodePosition < existing.costCodePosition) {
            existing.costCodePosition = costCodePosition;
        }
    });
    return billInfo;
};

/**
 * Orders a batch of pulled attachments into cost-code order:
 *  - Attachments are grouped by their source entity.
 *  - Blocks are ordered by the entity's first-occurring cost code on the line item table.
 */
const orderPulledFilesByCostCode = (
    pulledFiles: BTFileSystem[],
    lineItems: LineItemForInvoice[],
): BTFileSystem[] => {
    const billInfo = buildBillOrderingInfo(lineItems);
    return pulledFiles
        .map((file, incomingIndex) => ({ file, incomingIndex }))
        .sort((a, b) => {
            const aInfo = a.file.sourceEntityId ? billInfo.get(a.file.sourceEntityId) : undefined;
            const bInfo = b.file.sourceEntityId ? billInfo.get(b.file.sourceEntityId) : undefined;
            // Files with no resolvable entity sort last, keeping their incoming order.
            if (!aInfo && !bInfo) {
                return a.incomingIndex - b.incomingIndex;
            }
            if (!aInfo) {
                return 1;
            }
            if (!bInfo) {
                return -1;
            }
            if (aInfo.costCodePosition !== bInfo.costCodePosition) {
                return aInfo.costCodePosition - bInfo.costCodePosition;
            }
            if (a.file.sourceEntityId !== b.file.sourceEntityId) {
                return aInfo.selectionOrder - bInfo.selectionOrder;
            }
            // Same entity: keep attachments in the same order the parent entity has them as
            return a.incomingIndex - b.incomingIndex;
        })
        .map((entry) => entry.file);
};

/**
 * Sets the viewing order of the attachments based on where the related entity appears on the line item table
 *
 * @param newFiles     The freshly pulled entity attachments (carry sourceEntityId).
 * @param existingFiles The attachments already on the invoice before this add.
 * @param lineItems    The invoice line items, in line item table order.
 */
export const assignViewingOrderForAttachments = (
    newFiles: BTFileSystem[],
    existingFiles: BTFileSystem[],
    lineItems: LineItemForInvoice[],
): BTFileSystem[] => {
    const isInitialAdd = existingFiles.length === 0;
    const orderedNewFiles = isInitialAdd
        ? orderPulledFilesByCostCode(newFiles, lineItems)
        : newFiles;
    const orderedFiles = [...existingFiles, ...orderedNewFiles];
    return orderedFiles.map((file, index) => {
        file.viewingOrder = index;
        return file;
    });
};

/** Progress invoices always keep attachments in a saved order; standard invoices do only under Upmarket Sale Enablement. */
export const shouldOrderAttachments = (
    invoiceFormat: InvoiceFormat | undefined,
    isUpmarketSaleEnablementEnabled: boolean,
): boolean => invoiceFormat === InvoiceFormat.ProgressInvoice || isUpmarketSaleEnablementEnabled;

/**
 * The invoice API returns attachments already in their saved order, with no order number on them: the order
 * is the sequence they arrive in (files never reordered come newest first). Numbers that sequence so a later
 * upload or drag has a position to build on. A list where every file already carries an order is returned
 * untouched. In a mixed list (files from the server plus a later upload) the files without an order go
 * first, in the order received, ahead of the ordered ones. Works on copies so opening an invoice never edits
 * the loaded entity.
 */
export const seedViewingOrderFromReceivedOrder = (files: BTFileSystem[]): BTFileSystem[] => {
    const unordered = files.filter((file) => file.viewingOrder == null);
    if (unordered.length === 0) {
        return files;
    }
    const ordered = files
        .filter((file) => file.viewingOrder != null)
        .sort((a, b) => a.viewingOrder! - b.viewingOrder!);
    return [...unordered, ...ordered].map((file, index) =>
        // BTFileSystem has no copy method; the prototype is kept so the clone is still a BTFileSystem.
        Object.assign(Object.create(Object.getPrototypeOf(file) as object) as BTFileSystem, file, {
            viewingOrder: index,
        }),
    );
};

export function getChangeOrderTitleForPrefill(
    addedLineItems: LineItemForInvoice[],
): string | undefined {
    const coLineItems = addedLineItems.filter(
        (
            li,
        ): li is LineItemForInvoice & {
            parentEntity: NonNullable<LineItemForInvoice["parentEntity"]>;
        } => li.lineItemType === LineItemType.ChangeOrder && !!li.parentEntity,
    );
    if (coLineItems.length === 0) {
        return undefined;
    }
    const firstParentId = coLineItems[0].parentEntity.id;
    const allSameCo = coLineItems.every((li) => li.parentEntity.id === firstParentId);
    if (!allSameCo) {
        return undefined;
    }
    // Guard against a change order with a blank title so we never prefill whitespace.
    const title = coLineItems[0].parentEntity.title;
    return title && title.trim() ? title : undefined;
}

export function zeroUnknownMargins<T extends { margin?: number; markupPercent?: number }>(
    lineItems: T[],
): T[] {
    for (const lineItem of lineItems) {
        // Add-from lines (change orders, estimates) can carry a markup % with no margin; recalculation
        // derives the margin from that %, so zeroing it would price the line at cost.
        if (isNullOrUndefined(lineItem.markupPercent)) {
            lineItem.margin ??= 0;
        }
    }
    return lineItems;
}
